
import React from 'react';
import { startOfDay, endOfDay, isSameDay } from 'date-fns';
import { Event } from '@/lib/types';
import { isMultiDayEvent, getEventsForDay, isEventInDateRange } from '@/utils/eventUtils';
import { processMultiDayEvents, getMaxVerticalPosition } from '@/utils/calendarEventLayout';
import CalendarDayCell from '@/components/calendar/CalendarDayCell';
import CalendarGapCell from '@/components/calendar/CalendarGapCell';
import MultiDayEventsLayer from '@/components/calendar/MultiDayEventsLayer';
import { CalendarSlot } from '@/utils/calendarSlots';

interface CalendarBodyProps {
  slots: CalendarSlot[];
  days: Date[];
  daysToShow: number;
  cellWidth: number;
  filteredEvents: Event[];
  onEventClick: (event: Event) => void;
}

const CalendarBody = ({
  slots,
  days,
  daysToShow,
  cellWidth,
  filteredEvents,
  onEventClick
}: CalendarBodyProps) => {
  if (days.length === 0) {
    return null;
  }

  // Define the visible date range
  const startDate = startOfDay(days[0]);
  const endDate = endOfDay(days[days.length - 1]);
  const today = new Date();

  // Filter events to only include those in the visible date range
  const visibleEvents = filteredEvents.filter(event =>
    isEventInDateRange(event, startDate, endDate)
  );

  const hasAnyEvents = visibleEvents.length > 0;

  // Multi-day positioning continues to use the original `days[]` indices so the
  // absolute overlay lines up with the underlying grid (gap cells preserve the
  // total width).
  const multiDayEvents = hasAnyEvents ? visibleEvents.filter(isMultiDayEvent) : [];
  const processedMultiDayEvents = hasAnyEvents
    ? processMultiDayEvents(multiDayEvents, days)
    : [];
  const multiDayEventIds = new Set(processedMultiDayEvents.map(e => e.event.id));
  const maxVerticalPosition = getMaxVerticalPosition(processedMultiDayEvents);
  const topMargin = (maxVerticalPosition + 1) * 32 + 8;

  return (
    <div className="relative font-sans">
      <div
        className="flex border border-t-0 border-medihub-midPurple/20 rounded-b-lg"
        style={{ minHeight: `${Math.max(180, (maxVerticalPosition + 1) * 32 + 180)}px` }}
      >
        {slots.map((slot, slotIndex) => {
          if (slot.type === 'gap') {
            return (
              <CalendarGapCell
                key={`gap-${slotIndex}`}
                count={slot.spanDays}
                cellWidth={cellWidth}
                topMargin={topMargin}
              />
            );
          }

          const day = slot.day;
          const dayEvents = hasAnyEvents
            ? getEventsForDay(visibleEvents, day)
                .filter(event => !multiDayEventIds.has(event.id))
                .filter(event => !isMultiDayEvent(event))
            : [];

          return (
            <CalendarDayCell
              key={`day-${slotIndex}-${slot.originalIndex}`}
              day={day}
              index={slot.originalIndex}
              dayEvents={dayEvents}
              isEmpty={slot.isEmpty}
              isToday={isSameDay(day, today)}
              cellWidth={cellWidth}
              maxVerticalPosition={maxVerticalPosition}
              onEventClick={onEventClick}
            />
          );
        })}
      </div>

      {hasAnyEvents && (
        <MultiDayEventsLayer
          processedEvents={processedMultiDayEvents}
          slots={slots}
          onEventClick={onEventClick}
          cellWidth={cellWidth}
        />
      )}
    </div>
  );
};

export default CalendarBody;
